import React, { useState } from "react";
import styled from "styled-components";
import {
  ButtonLink,
  ButtonRound,
  CloseIcon,
  DescriptionList,
  InputDragDrop,
  InputMoney,
  InvoiceIcon,
  ModalForm,
  Text,
  unMask,
} from "@ficatranquilo/contabilidade.com-ui";
import { formatPeriodLabel } from "@helpers/competencePeriod";
import { formatBRL, formatCompetencia } from "../ow";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// Cartão do arquivo escolhido: nome (abre a prévia) e remover
const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 12px 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.N100};
  border-radius: 10px;
`;

const FileName = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  > svg {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.N400};
  }
`;

const DropIcon = styled(InvoiceIcon)`
  width: 40px;
  height: 40px;
`;

const PDF_ACCEPT = { "application/pdf": [".pdf"] };

/**
 * Modal reutilizável para upload de Nota Fiscal. Só monta com `show`.
 * Props:
 * - show: boolean
 * - onHide: function
 * - schedule: object (payment schedule selecionado)
 * - nfeService: service instance
 * - onSuccess: function callback após upload
 */
export default function TaxInvoiceUploadModal({ show, ...props }) {
  if (!show) return null;
  return <TaxInvoiceUploadForm {...props} />;
}

function TaxInvoiceUploadForm({ onHide, schedule, nfeService, onSuccess }) {
  // Com cronograma, o valor é o dele; sem, o prestador informa (máscara do InputMoney)
  const [value, setValue] = useState(() => (schedule?.expected_value ? parseFloat(schedule.expected_value).toFixed(2) : ""));
  const [file, setFile] = useState(null);
  const [loadingCreate, setLoadingCreate] = useState(false);

  const numericValue = schedule ? Number(value) : Number(unMask(value, true));

  async function handleSubmit() {
    if (!file || !(numericValue > 0)) return;
    setLoadingCreate(true);

    // Mesmo payload do formulário antigo: arquivo em "file", valor e cronograma
    const formData = new FormData();
    formData.append("file", file);
    formData.append("value", schedule ? value : String(numericValue));
    if (schedule?.id) {
      formData.append("payment_schedule_id", schedule.id);
    }

    try {
      const res = await nfeService.uploadTaxInvoice(formData);
      if (res && !res.error) {
        setLoadingCreate(false);
        onHide();
        onSuccess && onSuccess();
        return;
      }
    } catch (err) {
      console.error(err);
    }
    setLoadingCreate(false);
  }

  function handleClose() {
    if (loadingCreate) return;
    onHide();
  }

  function openPreview() {
    const href = URL.createObjectURL(file);
    window.open(href, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(href), 60000);
  }

  const summary = schedule
    ? [
        { label: "Competência", value: formatCompetencia({ month: Number(schedule.reference_month), year: Number(schedule.reference_year) }, true) },
        ...(schedule.period_start && schedule.period_end
          ? [{ label: "Horas apuradas", value: formatPeriodLabel(schedule.period_start, schedule.period_end) }]
          : []),
        { label: "Valor", value: formatBRL(value) },
      ]
    : null;

  return (
    <ModalForm
      $showModal
      $setShowModal={handleClose}
      $noClose={loadingCreate}
      $isLoading={loadingCreate}
      $size="medium"
      $title="Enviar nota fiscal"
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: handleClose, variant: "outline", isDisabled: loadingCreate },
        { text: "Enviar nota", action: handleSubmit, variant: "primary", isDisabled: loadingCreate || !file || !(numericValue > 0) },
      ]}
    >
      <Body>
        <Text
          $text="A nota vai para a empresa aprovar. Depois de aprovada, ela entra no pagamento da competência."
          $variant="paragraph_small_medium"
          $color="N700"
        />
        {summary ? (
          <DescriptionList $columns={summary.length} $items={summary} />
        ) : (
          <InputMoney
            $idForm="tax-invoice-value"
            $label="Valor"
            $variant="default"
            $isRequired
            placeholder="R$ 0,00"
            inputMode="numeric"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
        {/* Sem limite de tamanho: o back não limita e a tela antiga também não */}
        {file ? (
          <FileItem>
            <FileName>
              <InvoiceIcon />
              <ButtonLink $text={file.name} $action={openPreview} $noPadding $noWidth />
            </FileName>
            <ButtonRound title="Remover arquivo" aria-label="Remover arquivo" $variant="outline" $icon={<CloseIcon />} $noWidth $action={() => setFile(null)} />
          </FileItem>
        ) : (
          <InputDragDrop
            accept={PDF_ACCEPT}
            typeFile={{ valid: "application/pdf", message: " · São aceitos apenas arquivos em PDF" }}
            $icon={<DropIcon />}
            $message="Arraste o PDF da nota fiscal ou <strong>clique para escolher</strong>"
            onDrop={(accepted) => {
              if (accepted[0]) setFile(accepted[0]);
            }}
          />
        )}
        {summary ? (
          <Text
            $text="O valor da nota é o do cronograma de pagamento. Se ele estiver errado, fale com a empresa antes de enviar."
            $variant="caption_medium"
            $color="N400"
          />
        ) : null}
      </Body>
    </ModalForm>
  );
}
